import {
  ArrowRight,
  CheckCircle,
  MagnifyingGlass,
  Info,
  LinkSimple,
  ShieldCheck,
  WarningCircle,
  type Icon,
} from "@phosphor-icons/react";
import { useCallback, useState, type FormEvent } from "react";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { useAssessmentSession } from "../features/session/AssessmentSessionContext";
import {
  analyzeRejectionCode,
  type RejectionAnalysis,
} from "../services/api";

const fieldClass =
  "liquid-glass-field w-full rounded-2xl border border-white/[.1] bg-white/[.045] px-4 py-3.5 text-sm text-white outline-none placeholder:text-[#59605D] focus:border-brand/45 focus:ring-2 focus:ring-brand/10";
const benefits: [Icon, string][] = [
  [MagnifyingGlass, "Code-specific meaning and eligibility"],
  [CheckCircle, "Prioritized corrective actions"],
  [ShieldCheck, "Trusted documentation links"],
];

export function RejectionCheckPage() {
  const { credentials, startNew } = useAssessmentSession();
  const [code, setCode] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [usesDba, setUsesDba] = useState(false);
  const [details, setDetails] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [analysis, setAnalysis] = useState<RejectionAnalysis | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const onToken = useCallback((token: string) => setTurnstileToken(token), []);
  const mockMode =
    import.meta.env.VITE_USE_MOCK_ASSESSMENT === "true" ||
    (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_ASSESSMENT !== "false");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setAnalysis(null);
    if (!/^\d{5}$/.test(code)) {
      setError("Enter the complete five-digit rejection code.");
      return;
    }
    if (!businessName.trim()) {
      setError(`Enter the ${usesDba ? "DBA" : "legal business"} name used in the declined campaign.`);
      return;
    }
    if (!mockMode && !turnstileToken) {
      setError("Complete the bot-protection check before analyzing.");
      return;
    }
    setBusy(true);
    try {
      const session = credentials ?? (await startNew());
      const result = await analyzeRejectionCode(session, {
        code,
        businessName: businessName.trim(),
        usesDba,
        rejectionDetails: details.trim(),
        turnstileToken: turnstileToken || "mock-development-token",
      });
      setAnalysis(result);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to analyze this code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="relative isolate min-h-[calc(100vh-86px)] overflow-hidden px-5 py-16 sm:px-8 sm:py-24">
      <div className="pointer-events-none absolute left-1/2 top-[-24rem] -z-10 h-[52rem] w-[70rem] -translate-x-1/2 rounded-full bg-brand/[.1] blur-[130px]" />
      <div className="mx-auto max-w-[1240px]">
        <div className="grid items-start gap-12 lg:grid-cols-[.82fr_1.18fr]">
          <section>
            <div className="flex flex-wrap gap-3">
              <p className="eyebrow">Rejection analysis</p>
              <span className="dashboard-tag">Low Volume Mixed · DBA optional</span>
            </div>
            <h1 className="mt-6 max-w-2xl text-[2.7rem] font-medium leading-[1.02] tracking-[-.055em] sm:text-[4.4rem]">
              Turn a rejection code into a correction plan.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-[#A0A5A8]">
              Submit the exact code from GoHighLevel. The production workflow
              sends it securely to the A2bait Worker for source-grounded analysis against
              current provider guidance.
            </p>
            <div className="mt-9 space-y-4 text-sm text-[#A0A5A8]">
              {benefits.map(([ItemIcon, label]) => {
                return (
                  <div key={label} className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-brand/10 text-brand">
                      <ItemIcon size={18} />
                    </span>
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="space-y-5">
            <Card className="liquid-preview p-6 sm:p-8">
              <div className="flex items-center justify-between gap-4 border-b border-white/[.09] pb-5">
                <div>
                  <p className="eyebrow">Declined campaign</p>
                  <h2 className="mt-2 text-2xl font-semibold">Analyze the rejection</h2>
                </div>
                <span className="grid h-11 w-11 place-items-center rounded-full bg-brand/10 text-brand">
                  <WarningCircle size={23} />
                </span>
              </div>
              <form className="mt-6 space-y-5" onSubmit={submit}>
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block text-sm font-medium">
                    Rejection code
                    <input
                      className={`${fieldClass} mt-2 font-mono tracking-[.18em]`}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={5}
                      placeholder="30909"
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                    />
                  </label>
                  <label className="block text-sm font-medium">
                    {usesDba ? "DBA name" : "Legal business name"}
                    <input
                      className={`${fieldClass} mt-2`}
                      maxLength={250}
                      placeholder={usesDba ? "Your customer-facing DBA" : "Your registered business name"}
                      value={businessName}
                      onChange={(event) => setBusinessName(event.target.value)}
                    />
                  </label>
                </div>
                <label className="liquid-glass-subtle flex cursor-pointer items-center gap-3 rounded-2xl border border-white/[.08] p-4 text-sm">
                  <input
                    type="checkbox"
                    checked={usesDba}
                    onChange={(event) => setUsesDba(event.target.checked)}
                    className="h-4 w-4 accent-[#A8E61D]"
                  />
                  This declined campaign used a DBA name
                </label>
                <label className="block text-sm font-medium">
                  Rejection message <span className="text-[#70777B]">(optional)</span>
                  <textarea
                    className={`${fieldClass} mt-2 min-h-28 resize-y`}
                    maxLength={4000}
                    placeholder="Paste the rejection detail shown in the Trust Center. Do not include an EIN or other sensitive identifiers."
                    value={details}
                    onChange={(event) => setDetails(event.target.value)}
                  />
                </label>
                {!mockMode && <TurnstileWidget onToken={onToken} />}
                {error && (
                  <p role="alert" className="rounded-xl border border-red-400/20 bg-red-400/[.07] px-4 py-3 text-sm text-red-200">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={busy} className="w-full sm:w-auto">
                  {busy ? "Analyzing…" : "Analyze rejection"}
                  {!busy && <ArrowRight size={18} />}
                </Button>
              </form>
            </Card>

            {analysis && (
              <Card className="p-6 sm:p-8" aria-live="polite">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="eyebrow">Code {analysis.code}</p>
                    <h2 className="mt-2 text-2xl font-semibold">{analysis.category}</h2>
                  </div>
                  <span className="rounded-full border border-brand/25 bg-brand/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-brand">
                    {analysis.eligibility.replaceAll("_", " ")}
                  </span>
                </div>
                <p className="mt-5 leading-7 text-[#C5CBC8]">{analysis.summary}</p>
                <h3 className="mt-7 text-sm font-bold uppercase tracking-[.12em] text-[#8D9591]">
                  Correction plan
                </h3>
                <ol className="mt-4 space-y-3">
                  {analysis.correctiveActions.map((action, index) => (
                    <li key={action} className="liquid-glass-subtle flex gap-3 rounded-2xl border border-white/[.07] p-4 text-sm leading-6">
                      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand/10 font-bold text-brand">
                        {index + 1}
                      </span>
                      {action}
                    </li>
                  ))}
                </ol>
                <div className="mt-7 border-t border-white/[.08] pt-5">
                  <p className="text-xs font-bold uppercase tracking-[.12em] text-[#70777B]">Sources used</p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {analysis.sources.map((source) => (
                      <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-[#D8FF7D]">
                        <LinkSimple size={16} /> {source.title}
                      </a>
                    ))}
                  </div>
                </div>
              </Card>
            )}
            <div className="flex items-start gap-3 text-xs leading-5 text-[#70777B]">
              <Info size={17} className="mt-0.5 shrink-0" />
              <p>
                Independent guidance only. Results do not guarantee approval and
                should be checked against the latest provider documentation before
                resubmission. Do not enter EINs, tax IDs, or personal identifiers.
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
